import { useEffect, useState } from 'react';
import type { DataEntryColumnData, DataEntryComponent } from '../../../../api/pages-client/pages-client.type';
import { DataEntryDataType } from '../../../../api/pages-client/pages-client.type';
import i18n from '../../../../base-dictionary';

interface DataEntryWidgetForm {
  id?: string;
  name: string;
  description: string;
  column_data: DataEntryColumnData[];
}

interface ValidationErrors {
  name?: string;
  description?: string;
  column_data?: { [index: number]: { name?: string; data_type?: string } };
}

interface ConfirmationModalState {
  isOpen: boolean;
  type: 'delete' | 'rename';
  columnIndex: number;
  columnName: string;
  newName?: string;
}

const baseForm: DataEntryWidgetForm = {
  name: '',
  description: '',
  column_data: [],
};

export const useDataEntryModal = (
  opened: boolean,
  initialData?: DataEntryComponent,
  isEditing?: boolean,
  onSave?: (widget: DataEntryComponent) => void,
  onClose?: () => void,
) => {
  const widgetString = i18n.t('common.dashboard.widget');
  const [formData, setFormData] = useState<DataEntryWidgetForm>(baseForm);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalState>({
    isOpen: false,
    type: 'delete',
    columnIndex: -1,
    columnName: '',
  });

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    if (opened) {
      if (initialData && isEditing) {
        setFormData({
          id: initialData.id,
          name: initialData.name || '',
          description: initialData.description || '',
          column_data: initialData.column_data || [],
        });
      } else {
        setFormData(baseForm);
      }
      setErrors({});
    }
  }, [opened, initialData, isEditing]);

  const handleClose = () => {
    setFormData(baseForm);
    setErrors({});
    onClose?.();
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Validate widget name
    if (!formData.name.trim()) {
      newErrors.name = `${widgetString} name is required`;
    }

    // Validate column data
    if (formData.column_data.length === 0) {
      newErrors.column_data = {};
    } else {
      const columnErrors: { [index: number]: { name?: string; data_type?: string } } = {};

      formData.column_data.forEach((column, index) => {
        const columnError: { name?: string; data_type?: string } = {};

        if (!column.name.trim()) {
          columnError.name = 'Column name is required';
        }

        if (!column.data_type) {
          columnError.data_type = 'Data type is required';
        }

        // Check for duplicate column names
        const duplicateIndex = formData.column_data.findIndex(
          (col, idx) => idx !== index && col.name.trim().toLowerCase() === column.name.trim().toLowerCase(),
        );
        if (duplicateIndex !== -1 && column.name.trim()) {
          columnError.name = 'Column name must be unique';
        }

        if (columnError.name || columnError.data_type) {
          columnErrors[index] = columnError;
        }
      });

      if (Object.keys(columnErrors).length > 0) {
        newErrors.column_data = columnErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const widgetData: DataEntryComponent = {
        id: formData.id,
        page: initialData?.page || '',
        name: formData.name.trim(),
        description: formData.description.trim(),
        column_data: formData.column_data.map((col) => ({
          ...col,
          name: col.name.trim(),
        })),
        stored_data: initialData?.stored_data ?? [],
      };

      await onSave?.(widgetData);
      handleClose();
    } catch (error) {
      console.error('Error saving widget:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (value: string) => {
    setFormData({ ...formData, name: value });
    if (errors.name) {
      setErrors({ ...errors, name: undefined });
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData({ ...formData, description: value });
    if (errors.description) {
      setErrors({ ...errors, description: undefined });
    }
  };

  const isExistingColumn = (columnName: string): boolean => {
    if (!isEditing || !initialData?.column_data) return false;
    return initialData.column_data.some((col) => col.name.trim().toLowerCase() === columnName.trim().toLowerCase());
  };

  const handleColumnNameChange = (index: number, value: string) => {
    const currentColumn = formData.column_data[index];
    const originalColumnName = currentColumn.name;

    // Check if this is renaming an existing column
    if (
      isEditing &&
      originalColumnName &&
      isExistingColumn(originalColumnName) &&
      value.trim() !== originalColumnName.trim()
    ) {
      setConfirmationModal({
        isOpen: true,
        type: 'rename',
        columnIndex: index,
        columnName: originalColumnName,
        newName: value,
      });
      return;
    }

    // Proceed with normal name change
    const updatedColumns = [...formData.column_data];
    updatedColumns[index] = { ...updatedColumns[index], name: value };
    setFormData({ ...formData, column_data: updatedColumns });

    // Re-validate all column names to clear duplicate errors properly
    const newErrors = { ...errors };
    if (newErrors.column_data) {
      // Clear all column name errors and re-validate
      const columnErrors: { [index: number]: { name?: string; data_type?: string } } = {};

      updatedColumns.forEach((column, colIndex) => {
        const existingError = newErrors.column_data?.[colIndex];
        const columnError: { name?: string; data_type?: string } = {};

        // Keep data_type error if it exists
        if (existingError?.data_type) {
          columnError.data_type = existingError.data_type;
        }

        // Check for duplicates with updated data
        const duplicateIndex = updatedColumns.findIndex(
          (col, idx) => idx !== colIndex && col.name.trim().toLowerCase() === column.name.trim().toLowerCase(),
        );

        if (duplicateIndex !== -1 && column.name.trim()) {
          columnError.name = 'Column name must be unique';
        } else if (!column.name.trim()) {
          columnError.name = 'Column name is required';
        }

        if (columnError.name || columnError.data_type) {
          columnErrors[colIndex] = columnError;
        }
      });

      if (Object.keys(columnErrors).length > 0) {
        newErrors.column_data = columnErrors;
      } else {
        delete newErrors.column_data;
      }

      setErrors(newErrors);
    }
  };

  const handleColumnDataTypeChange = (index: number, value: DataEntryDataType) => {
    const updatedColumns = [...formData.column_data];
    updatedColumns[index] = { ...updatedColumns[index], data_type: value };
    setFormData({ ...formData, column_data: updatedColumns });

    // Clear errors for this column
    if (errors.column_data?.[index]?.data_type) {
      const newErrors = { ...errors };
      if (newErrors.column_data?.[index]) {
        delete newErrors.column_data[index].data_type;
        if (!newErrors.column_data[index].name) {
          delete newErrors.column_data[index];
        }
        setErrors(newErrors);
      }
    }
  };

  const removeColumn = (index: number) => {
    const columnToRemove = formData.column_data[index];

    // Check if this is an existing column with data
    if (isEditing && columnToRemove.name && isExistingColumn(columnToRemove.name)) {
      setConfirmationModal({
        isOpen: true,
        type: 'delete',
        columnIndex: index,
        columnName: columnToRemove.name,
      });
      return;
    }

    // Proceed with normal removal
    const updatedColumns = formData.column_data.filter((_, i) => i !== index);
    setFormData({ ...formData, column_data: updatedColumns });

    // Clear errors for removed column
    if (errors.column_data?.[index]) {
      const newErrors = { ...errors };
      if (newErrors.column_data) {
        delete newErrors.column_data[index];
        setErrors(newErrors);
      }
    }
  };

  const handleConfirmAction = () => {
    const { type, columnIndex, newName } = confirmationModal;

    if (type === 'delete') {
      // Proceed with column deletion
      const updatedColumns = formData.column_data.filter((_, i) => i !== columnIndex);
      setFormData({ ...formData, column_data: updatedColumns });

      // Clear errors for removed column
      if (errors.column_data?.[columnIndex]) {
        const newErrors = { ...errors };
        if (newErrors.column_data) {
          delete newErrors.column_data[columnIndex];
          setErrors(newErrors);
        }
      }
    } else if (type === 'rename' && newName !== undefined) {
      // Proceed with column rename
      const updatedColumns = [...formData.column_data];
      updatedColumns[columnIndex] = { ...updatedColumns[columnIndex], name: newName };
      setFormData({ ...formData, column_data: updatedColumns });

      // Re-validate column names
      const newErrors = { ...errors };
      if (newErrors.column_data) {
        const columnErrors: { [index: number]: { name?: string; data_type?: string } } = {};

        updatedColumns.forEach((column, colIndex) => {
          const existingError = newErrors.column_data?.[colIndex];
          const columnError: { name?: string; data_type?: string } = {};

          if (existingError?.data_type) {
            columnError.data_type = existingError.data_type;
          }

          const duplicateIndex = updatedColumns.findIndex(
            (col, idx) => idx !== colIndex && col.name.trim().toLowerCase() === column.name.trim().toLowerCase(),
          );

          if (duplicateIndex !== -1 && column.name.trim()) {
            columnError.name = 'Column name must be unique';
          } else if (!column.name.trim()) {
            columnError.name = 'Column name is required';
          }

          if (columnError.name || columnError.data_type) {
            columnErrors[colIndex] = columnError;
          }
        });

        if (Object.keys(columnErrors).length > 0) {
          newErrors.column_data = columnErrors;
        } else {
          delete newErrors.column_data;
        }

        setErrors(newErrors);
      }
    }

    // Close confirmation modal
    setConfirmationModal({
      isOpen: false,
      type: 'delete',
      columnIndex: -1,
      columnName: '',
    });
  };

  const handleCancelConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      type: 'delete',
      columnIndex: -1,
      columnName: '',
    });
  };

  const addColumn = () => {
    setFormData({
      ...formData,
      column_data: [...formData.column_data, { name: '', data_type: DataEntryDataType.INTEGER }],
    });
  };

  const isFormValid =
    formData.name.trim() &&
    formData.column_data.length > 0 &&
    formData.column_data.every((col) => col.name.trim() && col.data_type) &&
    Object.keys(errors).length === 0;

  return {
    // State
    formData,
    errors,
    isSubmitting,
    confirmationModal,
    widgetString,
    isFormValid,
    // Handlers
    handleClose,
    handleSubmit,
    handleNameChange,
    handleDescriptionChange,
    handleColumnNameChange,
    handleColumnDataTypeChange,
    removeColumn,
    handleConfirmAction,
    handleCancelConfirmation,
    addColumn,
  };
};
